/**
 * PaymentTypeSelector Component
 *
 * Allows users to choose between recurring subscription and one-time payment.
 *
 * Features:
 * - Visual radio buttons with clear descriptions
 * - Shows pricing for each option
 * - Highlights recommended option (subscription)
 * - Accessible with proper ARIA labels
 */

import { useMemo } from 'react';

/**
 * Payment type options
 */
export type PaymentType = 'subscription' | 'one-time';

/**
 * Props for the PaymentTypeSelector component
 */
export interface PaymentTypeSelectorProps {
  /** Currently selected payment type */
  selected: PaymentType;
  /** Callback when payment type changes */
  onSelect: (type: PaymentType) => void;
  /** One-time payment price for display */
  oneTimePrice: number;
  /** Subscription monthly price for display */
  subscriptionPrice: number;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Whether subscription option is available (requires Plan ID) */
  subscriptionAvailable: boolean;
}

/**
 * PaymentTypeSelector Component
 *
 * Displays two options:
 * 1. Recurring monthly subscription (recommended)
 * 2. One-time payment (manual renewal)
 */
export function PaymentTypeSelector({
  selected,
  onSelect,
  oneTimePrice,
  subscriptionPrice,
  disabled = false,
  subscriptionAvailable,
}: PaymentTypeSelectorProps) {
  const oneTimePriceFormatted = useMemo(() => oneTimePrice.toFixed(2).replace('.', ','), [oneTimePrice]);
  const subscriptionPriceFormatted = useMemo(() => subscriptionPrice.toFixed(2).replace('.', ','), [subscriptionPrice]);
  
  // Calculate annual pricing
  const annualSubscriptionPrice = subscriptionPrice * 12;
  const annualSubscriptionPriceFormatted = useMemo(() => annualSubscriptionPrice.toFixed(2).replace('.', ','), [annualSubscriptionPrice]);
  
  // Calculate yearly comparison (one-time × 12 vs annual subscription)
  const yearlyOneTimeCost = oneTimePrice * 12;
  const yearlySavings = yearlyOneTimeCost - annualSubscriptionPrice;
  const savingsPercent = Math.round((yearlySavings / yearlyOneTimeCost) * 100);

  return (
    <div className="space-y-3" role="radiogroup" aria-label="Type de paiement">
      {/* Subscription option */}
      <label
        className={`relative flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
          !subscriptionAvailable
            ? 'opacity-50 cursor-not-allowed border-slate-200 bg-slate-50'
            : selected === 'subscription'
            ? 'border-primary-500 bg-primary-50'
            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
        } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
      >
        <input
          type="radio"
          name="paymentType"
          value="subscription"
          checked={selected === 'subscription'}
          onChange={() => onSelect('subscription')}
          disabled={disabled || !subscriptionAvailable}
          className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900">Abonnement annuel</span>
            {subscriptionAvailable && savingsPercent > 0 && (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                -{savingsPercent}% vs mensuel
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {subscriptionAvailable ? (
              <>
                <span className="font-semibold text-slate-700">{annualSubscriptionPriceFormatted}€/an</span>
                <span className="text-slate-500"> (soit {subscriptionPriceFormatted}€/mois)</span>
              </>
            ) : (
              <>Non disponible - Configuration PayPal requise</>
            )}
          </p>
          {subscriptionAvailable && (
            <>
              <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Renouvellement automatique annuel, annulable à tout moment</span>
              </div>
              {yearlySavings > 0 && (
                <div className="mt-2 text-xs text-green-700 bg-green-50 rounded px-2 py-1">
                  💰 Économisez {yearlySavings.toFixed(2).replace('.', ',')}€/an par rapport au paiement mensuel
                </div>
              )}
            </>
          )}
        </div>
        {selected === 'subscription' && subscriptionAvailable && (
          <div className="absolute top-3 right-3">
            <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </label>

      {/* One-time payment option */}
      <label
        className={`relative flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
          selected === 'one-time'
            ? 'border-primary-500 bg-primary-50'
            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
        } ${disabled ? 'pointer-events-none opacity-60' : ''}`}
      >
        <input
          type="radio"
          name="paymentType"
          value="one-time"
          checked={selected === 'one-time'}
          onChange={() => onSelect('one-time')}
          disabled={disabled}
          className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300"
        />
        <div className="flex-1">
          <span className="font-medium text-slate-900">Paiement mensuel</span>
          <p className="text-sm text-slate-500 mt-1">
            <span className="font-semibold text-slate-700">{oneTimePriceFormatted}€/mois</span>
            {' '}- Renouvellement manuel chaque mois
          </p>
          <div className="flex items-center gap-1 mt-2 text-xs text-amber-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Pensez à renouveler avant expiration</span>
          </div>
        </div>
        {selected === 'one-time' && (
          <div className="absolute top-3 right-3">
            <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </label>
    </div>
  );
}

export default PaymentTypeSelector;
